import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE, photoUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import ProviderCard from "../components/ProviderCard";
import { toast } from "sonner";
import { Plus, PawPrint, Bell, AlertTriangle, Trash2, Heart, Crown, QrCode, CalendarClock, Pencil, Printer, Download, ImagePlus, X, User } from "lucide-react";

const EMPTY_PET = { name: "", species: "Cão", breed: "", photo: "", birthdate: "", allergies: "", notes: "", city: "Joinville", vaccines: [], deworming: [], flea: [] };

function PetForm({ initial, onSave, saving }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd);
      set("photo", r.data.path);
      toast.success("Foto enviada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar a foto");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Nome</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="pet-name-input" /></div>
        <div>
          <Label>Espécie</Label>
          <Select value={form.species} onValueChange={(v) => set("species", v)}>
            <SelectTrigger className="mt-1" data-testid="pet-species-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["Cão", "Gato", "Ave", "Roedor", "Réptil", "Outro"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Raça</Label><Input value={form.breed} onChange={(e) => set("breed", e.target.value)} className="mt-1" data-testid="pet-breed-input" /></div>
        <div><Label>Nascimento</Label><Input type="date" value={form.birthdate} onChange={(e) => set("birthdate", e.target.value)} className="mt-1" data-testid="pet-birthdate-input" /></div>
      </div>
      <div>
        <Label>Foto do pet</Label>
        <div className="flex items-center gap-3 mt-1">
          {form.photo && <img src={photoUrl(form.photo)} alt="Foto do pet" className="w-16 h-16 rounded-xl object-cover border" data-testid="pet-photo-preview" />}
          <label className="flex-1 cursor-pointer">
            <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="pet-photo-file" />
            <div className="border-2 border-dashed border-brand-coral/40 rounded-xl px-4 py-3 text-center text-sm text-brand-coral hover:bg-brand-coralLight/50 transition-colors flex items-center justify-center gap-2">
              <ImagePlus className="w-4 h-4" /> {uploading ? "Enviando..." : "Tirar foto ou escolher da galeria"}
            </div>
          </label>
        </div>
        <Input value={form.photo} onChange={(e) => set("photo", e.target.value)} placeholder="ou cole a URL de uma imagem" className="mt-2 text-xs" data-testid="pet-photo-input" />
      </div>
      <div><Label>Alergias</Label><Input value={form.allergies} onChange={(e) => set("allergies", e.target.value)} className="mt-1" data-testid="pet-allergies-input" /></div>
      <div><Label>Anotações</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1" data-testid="pet-notes-input" /></div>

      <div className="border rounded-xl p-3 bg-brand-cream/50">
        <p className="text-sm font-semibold mb-2">Vacinas</p>
        {form.vaccines.map((v, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 mb-2 items-center">
            <Input value={v.name} placeholder="Nome" onChange={(e) => { const vs = [...form.vaccines]; vs[i] = { ...v, name: e.target.value }; set("vaccines", vs); }} data-testid={`vaccine-name-${i}`} />
            <Input type="date" value={v.date || ""} onChange={(e) => { const vs = [...form.vaccines]; vs[i] = { ...v, date: e.target.value }; set("vaccines", vs); }} />
            <Input type="date" value={v.next_due || ""} title="Próxima dose" onChange={(e) => { const vs = [...form.vaccines]; vs[i] = { ...v, next_due: e.target.value }; set("vaccines", vs); }} data-testid={`vaccine-due-${i}`} />
            <Button variant="ghost" size="icon" onClick={() => set("vaccines", form.vaccines.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-destructive" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => set("vaccines", [...form.vaccines, { name: "", date: "", next_due: "" }])} data-testid="add-vaccine-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar vacina
        </Button>
      </div>

      <div className="border rounded-xl p-3 bg-brand-cream/50">
        <p className="text-sm font-semibold mb-2">Vermífugo</p>
        {form.deworming.map((v, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 mb-2 items-center">
            <Input value={v.name} placeholder="Nome" onChange={(e) => { const vs = [...form.deworming]; vs[i] = { ...v, name: e.target.value }; set("deworming", vs); }} />
            <Input type="date" value={v.date || ""} onChange={(e) => { const vs = [...form.deworming]; vs[i] = { ...v, date: e.target.value }; set("deworming", vs); }} />
            <Input type="date" value={v.next_due || ""} onChange={(e) => { const vs = [...form.deworming]; vs[i] = { ...v, next_due: e.target.value }; set("deworming", vs); }} />
            <Button variant="ghost" size="icon" onClick={() => set("deworming", form.deworming.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-destructive" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => set("deworming", [...form.deworming, { name: "", date: "", next_due: "" }])} data-testid="add-deworming-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar vermífugo
        </Button>
      </div>

      <Button onClick={() => onSave(form)} disabled={saving || !form.name} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="pet-save-btn">
        {saving ? "Salvando..." : "Salvar pet"}
      </Button>
    </div>
  );
}

export default function TutorDashboard() {
  const { user, refresh } = useAuth();
  const [pets, setPets] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tags, setTags] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [observations, setObservations] = useState([]);
  const [meta, setMeta] = useState({ categories: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({ name: "", phone: "", city: "Joinville", address: { cep: "", rua: "", numero: "", complemento: "", bairro: "" } });

  const loadAll = () => {
    api.post("/mp/sync").catch(() => {});
    api.get("/pets").then((r) => setPets(r.data));
    api.get("/pets/reminders/list").then((r) => setReminders(r.data));
    api.get("/favorites").then((r) => setFavorites(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/tags").then((r) => setTags(r.data));
    api.get("/observations").then((r) => setObservations(r.data));
    api.get("/notifications").then((r) => setNotifications(r.data));
    api.get("/meta").then((r) => setMeta(r.data));
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", phone: user.phone || "", city: user.city || "Joinville",
                           address: { cep: "", rua: "", numero: "", complemento: "", bairro: "", ...(user.address || {}) } });
  }, [user]);

  const savePet = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/pets/${editing.pet_id}`, form);
        toast.success("Pet atualizado!");
      } else {
        await api.post("/pets", form);
        toast.success("Pet cadastrado!");
      }
      setDialogOpen(false);
      setEditing(null);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const deletePet = async (pet) => {
    if (!window.confirm(`Remover ${pet.name}?`)) return;
    await api.delete(`/pets/${pet.pet_id}`);
    toast.success("Pet removido");
    loadAll();
  };

  const toggleLost = async (pet) => {
    const active = !pet.lost?.active;
    let message = "", location = pet.city || "Joinville";
    if (active) {
      message = window.prompt("Mensagem para quem encontrar (ex: recompensa, comportamento):") || "";
      location = window.prompt("Último local visto (bairro/região):") || location;
      if (message === null) return;
    }
    await api.post(`/pets/${pet.pet_id}/lost`, { active, message, location });
    toast.success(active ? `${pet.name} marcado como desaparecido. A comunidade foi acionada.` : `${pet.name} foi encontrado! Que alegria.`);
    loadAll();
  };

  const toggleVolunteer = async (active) => {
    await api.post("/auth/volunteer", { active });
    await refresh();
    toast.success(active ? "Obrigado por ser voluntário!" : "Modo voluntário desativado");
  };

  const saveProfile = async () => {
    if (!profile.name.trim()) return toast.error("Informe seu nome");
    try {
      await api.put("/users/me", profile);
      await refresh();
      toast.success("Dados atualizados!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="tutor-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold font-heading text-brand-graphite">Olá, {user?.name?.split(" ")[0]}!</h1>
          <p className="text-muted-foreground mt-1">Sua central de cuidado pet</p>
        </div>
        {user?.club?.active ? (
          <span className="inline-flex items-center gap-2 bg-brand-mustardLight text-brand-mustardDark font-semibold text-sm px-4 py-2 rounded-full" data-testid="club-badge">
            <Crown className="w-4 h-4" /> PetHub Club {user.club.early_bird ? "Early Bird (R$ 9,90 vitalício)" : ""}
          </span>
        ) : (
          <Link to="/planos" data-testid="join-club-cta">
            <Button className="bg-brand-mustard hover:bg-brand-mustardDark text-brand-graphite rounded-full font-semibold">
              <Crown className="w-4 h-4 mr-2" /> Assinar PetHub Club — R$ 9,90 Early Bird
            </Button>
          </Link>
        )}
      </div>

      {notifications.length > 0 && (
        <div className="space-y-2 mb-8" data-testid="notifications-list">
          {notifications.slice(0, 5).map((n) => (
            <div key={n.notif_id} className={`rounded-xl px-4 py-3 text-sm flex items-start gap-3 ${n.type === "sighting" || n.type === "tag_found" ? "bg-red-50 border border-red-200 text-red-800" : "bg-brand-mintLight border border-brand-mint/30 text-brand-graphite"}`}>
              <Bell className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="flex-1"><strong>{n.title}</strong> — {n.message}</div>
              <button
                onClick={async () => {
                  await api.put(`/notifications/${n.notif_id}/dismiss`);
                  setNotifications((prev) => prev.filter((x) => x.notif_id !== n.notif_id));
                }}
                className="shrink-0 p-1 rounded-full hover:bg-black/5 transition-colors"
                title="Dispensar mensagem" data-testid={`dismiss-notif-${n.notif_id}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="pets">
        <TabsList className="bg-white border" data-testid="tutor-tabs">
          <TabsTrigger value="pets" data-testid="tab-pets"><PawPrint className="w-4 h-4 mr-1.5" />Meus Pets</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders"><CalendarClock className="w-4 h-4 mr-1.5" />Lembretes</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites"><Heart className="w-4 h-4 mr-1.5" />Favoritos</TabsTrigger>
          <TabsTrigger value="tags" data-testid="tab-tags"><QrCode className="w-4 h-4 mr-1.5" />Tags & Pedidos</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account"><User className="w-4 h-4 mr-1.5" />Minha Conta</TabsTrigger>
        </TabsList>

        <TabsContent value="pets" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pets.map((pet) => (
              <div key={pet.pet_id} className={`bg-white rounded-2xl overflow-hidden border card-hover ${pet.lost?.active ? "border-red-300 ring-2 ring-red-200" : "border-brand-coral/5"}`} data-testid={`pet-card-${pet.pet_id}`}>
                <div className="relative h-40 bg-brand-mintLight">
                  {pet.photo ? <img src={photoUrl(pet.photo)} alt={pet.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><PawPrint className="w-10 h-10 text-brand-mint" /></div>}
                  {pet.lost?.active && <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">DESAPARECIDO</span>}
                  {pet.tag_code && <span className="absolute top-3 right-3 bg-white/90 text-brand-graphite text-xs font-mono px-2 py-1 rounded-lg">{pet.tag_code}</span>}
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-heading font-bold text-lg text-brand-graphite">{pet.name}</h3>
                    <span className="text-xs text-muted-foreground">{pet.species}{pet.breed ? ` • ${pet.breed}` : ""}</span>
                  </div>
                  {pet.allergies && <p className="text-xs text-red-600 mt-1">Alergias: {pet.allergies}</p>}
                  {pet.vaccines?.length > 0 && <p className="text-xs text-muted-foreground mt-1">{pet.vaccines.length} vacina(s) registrada(s)</p>}
                  <div className="flex gap-2 mt-4">
                    <Dialog open={dialogOpen && editing?.pet_id === pet.pet_id} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="rounded-full" onClick={() => setEditing(pet)} data-testid={`pet-edit-${pet.pet_id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Editar {pet.name}</DialogTitle></DialogHeader>
                        <PetForm initial={{ ...EMPTY_PET, ...pet }} onSave={savePet} saving={saving} />
                      </DialogContent>
                    </Dialog>
                    <Button size="sm" variant={pet.lost?.active ? "default" : "outline"}
                      className={pet.lost?.active ? "rounded-full bg-brand-mint hover:bg-brand-mintDark text-white" : "rounded-full border-red-300 text-red-600 hover:bg-red-50"}
                      onClick={() => toggleLost(pet)} data-testid={`pet-lost-toggle-${pet.pet_id}`}>
                      <AlertTriangle className="w-3.5 h-3.5 mr-1" /> {pet.lost?.active ? "Encontrado!" : "Desapareceu"}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deletePet(pet)} data-testid={`pet-delete-${pet.pet_id}`}><Trash2 className="w-4 h-4 text-muted-foreground" /></Button>
                  </div>
                </div>
              </div>
            ))}
            <Dialog open={dialogOpen && !editing} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
              <DialogTrigger asChild>
                <button className="rounded-2xl border-2 border-dashed border-brand-coral/30 min-h-[280px] flex flex-col items-center justify-center text-brand-coral hover:bg-brand-coralLight/50 transition-colors" data-testid="add-pet-btn">
                  <Plus className="w-8 h-8 mb-2" />
                  <span className="font-heading font-semibold">Adicionar pet</span>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Novo pet</DialogTitle></DialogHeader>
                <PetForm initial={EMPTY_PET} onSave={savePet} saving={saving} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="mt-8 bg-white rounded-2xl p-5 border border-brand-coral/10 flex items-center justify-between" data-testid="volunteer-section">
            <div>
              <p className="font-heading font-semibold text-brand-graphite">Modo Voluntário</p>
              <p className="text-sm text-muted-foreground">Receba alertas quando um pet desaparecer perto de você e ajude a comunidade.</p>
            </div>
            <Switch checked={!!user?.volunteer} onCheckedChange={toggleVolunteer} data-testid="volunteer-switch" />
          </div>
        </TabsContent>

        <TabsContent value="reminders" className="mt-6">
          {reminders.length === 0 ? (
            <p className="text-muted-foreground py-10 text-center" data-testid="no-reminders">Nenhum lembrete nos próximos 30 dias. Tudo em dia!</p>
          ) : (
            <div className="space-y-3" data-testid="reminders-list">
              {reminders.map((r, i) => (
                <div key={i} className={`rounded-xl px-5 py-4 flex items-center justify-between ${r.overdue ? "bg-red-50 border border-red-200" : "bg-brand-mustardLight border border-brand-mustard/40"}`}>
                  <div>
                    <p className="font-semibold text-sm text-brand-graphite">{r.type}: {r.item} — {r.pet_name}</p>
                    <p className="text-xs text-muted-foreground">Vencimento: {r.due?.split("-").reverse().join("/")}</p>
                  </div>
                  {r.overdue && <span className="text-xs font-bold text-red-600 uppercase">Vencido</span>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="favorites" className="mt-6">
          {favorites.length === 0 ? (
            <p className="text-muted-foreground py-10 text-center" data-testid="no-favorites">Você ainda não favoritou nenhum prestador.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {favorites.map((p) => <ProviderCard key={p.provider_id} provider={p} categories={meta.categories} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="tags" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h3 className="font-heading font-semibold text-lg text-brand-graphite mb-3">Minhas Tags</h3>
              {tags.length === 0 ? (
                <div className="bg-white rounded-2xl p-6 border text-center">
                  <p className="text-sm text-muted-foreground mb-3">Nenhuma tag ainda.</p>
                  <Link to="/loja" data-testid="buy-tag-cta"><Button className="bg-brand-coral hover:bg-brand-coralDark text-white rounded-full">Comprar tag</Button></Link>
                </div>
              ) : (
                <div className="space-y-4" data-testid="tags-list">
                  {tags.map((t) => {
                    const pet = pets.find((p) => p.pet_id === t.pet_id);
                    const statusLabel = { pendente: "Pedido recebido", confeccao: "Em confecção", enviado: "Enviado", entregue: "Entregue" }[t.status || "pendente"];
                    return (
                      <div key={t.tag_code} className="bg-white rounded-xl p-4 border flex gap-4 items-center">
                        <img src={`${API_BASE}/tags/${t.tag_code}/qr.png`} alt={`QR ${t.tag_code}`} className="w-20 h-20 rounded-lg border shrink-0" data-testid={`tag-qr-${t.tag_code}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-mono font-bold text-brand-coral">{t.tag_code}</p>
                          <p className="text-xs text-muted-foreground">{pet ? `Vinculada a ${pet.name}` : "Não vinculada"} • {t.type}</p>
                          <p className="text-xs font-semibold text-brand-mintDark mt-1" data-testid={`tag-status-${t.tag_code}`}>{statusLabel}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <Link to={`/tag/${t.tag_code}`} data-testid={`view-tag-${t.tag_code}`}><Button variant="outline" size="sm" className="rounded-full h-7 text-xs">Página pública</Button></Link>
                            <a href={`${API_BASE}/tags/${t.tag_code}/qr.png`} download data-testid={`tag-qr-png-${t.tag_code}`}><Button variant="outline" size="sm" className="rounded-full h-7 text-xs"><Download className="w-3 h-3 mr-1" />QR PNG</Button></a>
                            <a href={`${API_BASE}/tags/${t.tag_code}/qr.svg`} download data-testid={`tag-qr-svg-${t.tag_code}`}><Button variant="outline" size="sm" className="rounded-full h-7 text-xs"><Download className="w-3 h-3 mr-1" />QR SVG (laser)</Button></a>
                            {pet && <Link to={`/cartaz/${pet.pet_id}`} data-testid={`poster-${pet.pet_id}`}><Button variant="outline" size="sm" className="rounded-full h-7 text-xs border-red-300 text-red-600"><Printer className="w-3 h-3 mr-1" /> Cartaz Procura-se</Button></Link>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {observations.length > 0 && (
                <div className="mt-6" data-testid="observations-list">
                  <h4 className="font-heading font-semibold text-sm text-brand-graphite mb-2">Observações de quem encontrou seus pets</h4>
                  <div className="space-y-2">
                    {observations.map((o) => (
                      <div key={o.obs_id} className="bg-brand-mustardLight rounded-xl px-4 py-3 text-sm">
                        <p className="text-brand-graphite">"{o.message}"</p>
                        <p className="text-xs text-muted-foreground mt-1">{o.created_at?.slice(0, 10).split("-").reverse().join("/")}{o.contact ? ` • Contato: ${o.contact}` : ""}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg text-brand-graphite mb-3">Meus pedidos</h3>
              <div className="space-y-3" data-testid="orders-list">
                {orders.length === 0 && <p className="text-sm text-muted-foreground">Nenhum pedido ainda.</p>}
                {orders.map((o) => {
                  const physical = ["tag", "collar", "anilha"].includes(o.kind);
                  const steps = ["aprovado", "preparando", "enviado", "entregue"];
                  const stepLabels = { aprovado: "Compra aprovada", preparando: "Em preparação", enviado: "Enviado", entregue: "Entregue" };
                  const idx = steps.indexOf(o.fulfillment_status || "aprovado");
                  return (
                    <div key={o.order_id} className="bg-white rounded-xl p-4 border" data-testid={`order-card-${o.order_id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sm text-brand-graphite">{o.item}</p>
                          <p className="text-xs text-muted-foreground">{o.created_at?.slice(0, 10).split("-").reverse().join("/")}</p>
                        </div>
                        <span className="font-heading font-semibold text-brand-mintDark">R$ {Number(o.amount).toFixed(2).replace(".", ",")}</span>
                      </div>
                      {o.status === "pending" ? (
                        <p className="text-xs font-semibold text-brand-mustardDark mt-2" data-testid={`order-status-${o.order_id}`}>Aguardando pagamento</p>
                      ) : physical ? (
                        <div className="mt-3" data-testid={`order-status-${o.order_id}`}>
                          <div className="flex items-center">
                            {steps.map((s, i) => (
                              <div key={s} className="flex items-center flex-1 last:flex-none">
                                <div className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 ${i <= idx ? "bg-brand-mint border-brand-mint" : "bg-white border-gray-300"}`} />
                                {i < steps.length - 1 && <div className={`h-0.5 flex-1 ${i < idx ? "bg-brand-mint" : "bg-gray-200"}`} />}
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between mt-1">
                            {steps.map((s, i) => (
                              <span key={s} className={`text-[10px] ${i === idx ? "font-bold text-brand-mintDark" : "text-muted-foreground"}`}>{stepLabels[s]}</span>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs font-semibold text-brand-mintDark mt-2" data-testid={`order-status-${o.order_id}`}>Confirmado</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="account" className="mt-6">
          <div className="max-w-xl bg-white rounded-2xl border p-6" data-testid="account-form">
            <h3 className="font-heading font-semibold text-lg text-brand-graphite mb-1">Meus dados</h3>
            <p className="text-xs text-muted-foreground mb-4">Mantenha seus dados atualizados — telefone e endereço são usados nas gravações e entregas dos seus pedidos.</p>
            <div className="space-y-3">
              <div><Label>Nome completo *</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1" data-testid="profile-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Telefone / WhatsApp</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="(47) 99999-9999" className="mt-1" data-testid="profile-phone-input" /></div>
                <div><Label>Cidade</Label><Input value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} className="mt-1" data-testid="profile-city-input" /></div>
              </div>
              <div className="border-t pt-3 mt-2">
                <p className="text-sm font-semibold text-brand-graphite mb-2">Endereço para entregas</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>CEP</Label><Input value={profile.address.cep} onChange={(e) => setProfile({ ...profile, address: { ...profile.address, cep: e.target.value } })} placeholder="89200-000" className="mt-1" data-testid="profile-cep-input" /></div>
                  <div><Label>Bairro</Label><Input value={profile.address.bairro} onChange={(e) => setProfile({ ...profile, address: { ...profile.address, bairro: e.target.value } })} className="mt-1" data-testid="profile-bairro-input" /></div>
                </div>
                <div className="mt-3"><Label>Rua</Label><Input value={profile.address.rua} onChange={(e) => setProfile({ ...profile, address: { ...profile.address, rua: e.target.value } })} className="mt-1" data-testid="profile-rua-input" /></div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div><Label>Número</Label><Input value={profile.address.numero} onChange={(e) => setProfile({ ...profile, address: { ...profile.address, numero: e.target.value } })} className="mt-1" data-testid="profile-numero-input" /></div>
                  <div><Label>Complemento</Label><Input value={profile.address.complemento} onChange={(e) => setProfile({ ...profile, address: { ...profile.address, complemento: e.target.value } })} className="mt-1" data-testid="profile-complemento-input" /></div>
                </div>
              </div>
              <Button onClick={saveProfile} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="save-profile-btn">Salvar alterações</Button>
              <p className="text-xs text-muted-foreground">E-mail da conta: <strong>{user?.email}</strong> (não pode ser alterado). Para excluir sua conta e seus dados (LGPD), fale com o suporte.</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
